import { DealStage, DealStageGroup, DealStageItem, isDealStageGroup } from "store/types";

/** Asset class is a plain string on the card; guard against null/undefined. */
export const getAssetClassName = (assetClass: DealStageItem["asset_class"] | null | undefined): string =>
  assetClass ? String(assetClass) : "";

export interface CardChip {
  key: string;
  value: string;
}

/**
 * Resolve the chip values for a card from the requested `card_characteristics` names.
 * Each name is a top-level key on the deal whose value is string | string[] | null — a multi-value
 * characteristic yields one chip per element. Values are read verbatim from the payload.
 */
export const getCardChips = (item: DealStageItem, names: string[]): CardChip[] => {
  const record = item as unknown as Record<string, unknown>;
  const chips: CardChip[] = [];

  names.forEach((name) => {
    const value = record[name];
    if (Array.isArray(value)) {
      value.forEach((entry, index) => {
        const text = String(entry).trim();
        if (text) chips.push({ key: `${name}-${index}`, value: text });
      });
    } else if (value !== null && value !== undefined && String(value).trim() !== "") {
      chips.push({ key: name, value: String(value) });
    }
  });

  return chips;
};

/** Stable key for a group inside a stage, from its group-title path — a deal can live in several groups. */
export const groupKeyPath = (stageId: number, titlePath: string[]): string => `${stageId}:${titlePath.join(">")}`;

/** Encode a group title path for use inside a draggableId (a deal can appear in several groups → unique ids). */
export const encodeGroupPath = (path: string[]): string => path.map(encodeURIComponent).join(">");

/** Every group key (incl. nested) rendered for a stage — used to Collapse All. */
export const collectGroupKeys = (stage: DealStage): string[] => {
  const keys: string[] = [];
  const walk = (nodes: Array<DealStageGroup | DealStageItem>, parentPath: string[]) => {
    nodes.forEach((node) => {
      if (isDealStageGroup(node)) {
        const path = [...parentPath, node.group_title];
        keys.push(groupKeyPath(stage.id, path));
        walk(node.children, path);
      }
    });
  };
  walk(stage.groups ?? [], []);
  return keys;
};
